import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type {
  AnomalyStats,
  ClassificationStats,
  Customer,
  StatsOverview,
  StatsWindow,
} from '@visionqc/shared';
import { TASKS } from '@visionqc/shared';
import { api } from '../api/client';
import { useProducts } from '../api/useProducts';
import {
  StackedColumns,
  VERDICT_SERIES,
  CLASSIFICATION_SERIES,
} from '../components/charts/StackedColumns';
import { DefectBars } from '../components/charts/DefectBars';
import { ClassBars } from '../components/charts/ClassBars';

const WINDOWS: Array<{ value: StatsWindow; label: string }> = [
  { value: '1h', label: 'Last hour' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  // Always shows something as long as anything was ever inspected — the fallback
  // for a quiet line, so the dashboard is never a blank page.
  { value: 'all', label: 'All time' },
];

/**
 * Line dashboard, split by the question the line is asking.
 *
 * A line can run both kinds of product, and the two are reported in separate
 * sections that never share a number. That is not a layout preference. When
 * they shared one, the pass rate was good over EVERY inspection — and a
 * classified crate has no verdict and never will, so each one quietly counted
 * against the rate. It read 34.5% where the line's real pass rate was 44.1%,
 * with nothing on screen to suggest two different questions had been added
 * together.
 *
 * So: no combined total, no combined throughput, and the word "pass rate"
 * appears only where verdicts exist. Filters sit in one row above both.
 */
export function DashboardView() {
  const { products } = useProducts();

  /**
   * The customer may arrive in the URL, because Reporting links here.
   *
   * Without this the link landed on "All customers" and looked like it had
   * ignored the click — the page it opened was correct for a different question
   * than the one asked. Kept in the URL rather than passed through router state
   * so the result is also a link somebody can send.
   */
  const [params, setParams] = useSearchParams();
  const fromUrl = Number(params.get('customer'));
  const [customerId, setCustomerId] = useState<number | undefined>(
    Number.isInteger(fromUrl) && fromUrl > 0 ? fromUrl : undefined,
  );
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [productId, setProductId] = useState<number | undefined>();
  // Default to all time: a quiet line makes a 24-hour view read as "nothing
  // works", where the point of the dashboard is that it always shows something.
  const [window, setWindow] = useState<StatsWindow>('all');
  const [stats, setStats] = useState<StatsOverview | null>(null);

  useEffect(() => {
    api.listCustomers().then(setCustomers).catch(() => setCustomers([]));
  }, []);

  const load = useCallback(() => {
    api.stats(window, productId, customerId).then(setStats).catch(() => setStats(null));
  }, [window, productId, customerId]);

  useEffect(() => {
    load();
    const id = setInterval(load, 10_000);
    return () => clearInterval(id);
  }, [load]);

  return (
    <div>
      <h1 className="view-title">Dashboard</h1>

      {/* Filters: one row, above everything. */}
      <div className="panel" style={{ marginBottom: 20 }}>
        <div className="row">
          <div>
            <label>Customer</label>
            <select
              value={customerId ?? ''}
              onChange={(e) => {
                const next = e.target.value ? Number(e.target.value) : undefined;
                setCustomerId(next);
                setProductId(undefined); // product list narrows to the customer
                // Keep the URL honest about what is on screen, so a reload or a
                // shared link shows the same thing.
                setParams(next ? { customer: String(next) } : {}, { replace: true });
              }}
            >
              <option value="">All customers</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Product</label>
            <select
              value={productId ?? ''}
              onChange={(e) => setProductId(e.target.value ? Number(e.target.value) : undefined)}
            >
              <option value="">All products</option>
              {products
                .filter((p) => !customerId || p.customerId === customerId)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label>Time window</label>
            <select value={window} onChange={(e) => setWindow(e.target.value as StatsWindow)}>
              {WINDOWS.map((w) => (
                <option key={w.value} value={w.value}>
                  {w.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {!stats ? (
        <div className="panel spinner">Loading…</div>
      ) : !stats.anomaly && !stats.classification ? (
        /*
          An empty window and an empty installation are different facts and used
          to print the same sentence. The dashboard opens on 24 hours, so a line
          that stopped two days ago showed "No inspections in this window" —
          which reads as broken software rather than as a quiet line, and was
          reported as exactly that.
        */
        <div className="panel">
          {stats.lastInspectionAt ? (
            <>
              <div style={{ fontSize: 16 }}>
                Nothing in {WINDOWS.find((w) => w.value === window)?.label.toLowerCase()}.
              </div>
              <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>
                The line has been quiet since{' '}
                <strong>{new Date(stats.lastInspectionAt).toLocaleString()}</strong> —{' '}
                {describeGap(stats.lastInspectionAt)}.
              </div>
              {window !== 'all' && (
                <button
                  className="btn sm primary"
                  style={{ marginTop: 12 }}
                  onClick={() => setWindow('all')}
                >
                  Show all time →
                </button>
              )}
            </>
          ) : (
            <span className="muted">
              Nothing has been inspected yet. Upload images on Dataset, or point a station at this
              server.
            </span>
          )}
        </div>
      ) : (
        <div className="grid" style={{ gap: 28 }}>
          {stats.anomaly && <AnomalySection stats={stats.anomaly} />}
          {stats.classification && <ClassificationSection stats={stats.classification} />}
        </div>
      )}
    </div>
  );
}

/**
 * A heading naming the question, shown only when both sections are on screen —
 * no, always. A line running one kind of product today may run the other
 * tomorrow, and a number that means "of the parts we judged" should say so
 * whether or not something else happens to be beside it.
 */
function SectionHead({ title, question }: { title: string; question: string }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <h2 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>{title}</h2>
      <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
        {question}
      </div>
    </div>
  );
}

function AnomalySection({ stats }: { stats: AnomalyStats }) {
  const [showTable, setShowTable] = useState(false);
  const navigate = useNavigate();
  const passRate = stats.total > 0 ? stats.goodRatio * 100 : null;

  return (
    <section>
      <SectionHead title={TASKS.anomaly.label} question={TASKS.anomaly.question} />
      <div className="grid" style={{ gap: 20 }}>
        <div className="row" style={{ gap: 16, alignItems: 'stretch' }}>
          <div
            className="stat-tile"
            style={
              {
                flex: '1 1 220px',
                ...(passRate === null ? {} : { ['--tone']: 'var(--good)' }),
              } as CSSProperties
            }
          >
            <div className="stat-label">Pass rate</div>
            <div
              className="stat-value"
              style={{ fontSize: 52, color: passRate === null ? 'var(--text-dim)' : undefined }}
            >
              {passRate === null ? '—' : `${passRate.toFixed(1)}%`}
            </div>
            <div className="muted" style={{ fontSize: 13 }}>
              {stats.good.toLocaleString()} of {stats.total.toLocaleString()} inspections
            </div>
          </div>
          <Tile label="Rejected" value={stats.bad} tone="var(--bad)" />
          <Tile label="Unsure" value={stats.unsure} tone="var(--warn)" />
          <Tile label="Throughput / min" value={stats.throughputPerMin.toFixed(2)} tone="var(--teal)" />
          <Tile label="Avg latency" value={`${stats.avgLatencyMs} ms`} tone="var(--violet)" />
        </div>

        {/* Annotation backlog — actionable, so it links to the work. */}
        {stats.unannotated > 0 && (
          <div className="panel row" style={{ justifyContent: 'space-between' }}>
            <span>
              <strong style={{ fontSize: 18 }}>{stats.unannotated.toLocaleString()}</strong>{' '}
              <span className="muted">inspections still need a human label</span>
            </span>
            <button className="btn primary sm" onClick={() => navigate('/annotate')}>
              Annotate now →
            </button>
          </div>
        )}

        <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)' }}>
          <div className="panel">
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 6 }}>
              <strong>Verdicts over time</strong>
              <button className="btn sm" onClick={() => setShowTable((t) => !t)}>
                {showTable ? 'Show chart' : 'Show table'}
              </button>
            </div>

            {showTable ? (
              <table>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Good</th>
                    <th>Unsure</th>
                    <th>Bad</th>
                    <th>Avg latency</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.buckets.map((b) => (
                    <tr key={b.t} style={{ cursor: 'default' }}>
                      <td className="muted">{new Date(b.t).toLocaleString()}</td>
                      <td>{b.good}</td>
                      <td>{b.unsure}</td>
                      <td>{b.bad}</td>
                      <td className="muted">{b.avgLatencyMs} ms</td>
                    </tr>
                  ))}
                  {stats.buckets.length === 0 && (
                    <tr>
                      <td colSpan={5} className="muted">
                        No data in this window.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            ) : (
              <StackedColumns
                buckets={stats.buckets}
                series={VERDICT_SERIES}
                emptyMessage="No inspections in this window yet."
                ariaLabel="Verdict composition over time"
              />
            )}
          </div>

          <div className="panel">
            <strong>Top defects found</strong>
            <p className="muted" style={{ fontSize: 12, margin: '4px 0 14px' }}>
              From operator annotations in this window.
            </p>
            <DefectBars defects={stats.defects} />
          </div>
        </div>
      </div>
    </section>
  );
}

function ClassificationSection({ stats }: { stats: ClassificationStats }) {
  const [showTable, setShowTable] = useState(false);
  const navigate = useNavigate();

  /**
   * The headline is agreement, not accuracy.
   *
   * Accuracy would need a truth for every crate and there is only a truth for
   * the ones somebody looked at. So this says what it is: of the predictions a
   * person has reviewed, how many they left alone. When nobody has reviewed
   * anything it is null and reads "—" rather than 100%, which is what dividing
   * by an empty set would otherwise flatter it into.
   */
  const reviewed = stats.predicted - stats.unreviewed;
  const agreement = reviewed > 0 ? ((reviewed - stats.corrected) / reviewed) * 100 : null;

  return (
    <section>
      <SectionHead title={TASKS.classification.label} question={TASKS.classification.question} />
      <div className="grid" style={{ gap: 20 }}>
        <div className="row" style={{ gap: 16, alignItems: 'stretch' }}>
          <div
            className="stat-tile"
            style={
              {
                flex: '1 1 220px',
                ...(agreement === null ? {} : { ['--tone']: 'var(--good)' }),
              } as CSSProperties
            }
          >
            <div className="stat-label">Agreement</div>
            <div
              className="stat-value"
              style={{ fontSize: 52, color: agreement === null ? 'var(--text-dim)' : undefined }}
            >
              {agreement === null ? '—' : `${agreement.toFixed(1)}%`}
            </div>
            <div className="muted" style={{ fontSize: 13 }}>
              {reviewed > 0
                ? `${(reviewed - stats.corrected).toLocaleString()} of ${reviewed.toLocaleString()} reviewed predictions kept`
                : 'nothing reviewed yet'}
            </div>
          </div>
          <Tile label="Classified" value={stats.predicted} tone="var(--accent)" />
          <Tile
            label="Sent to a person"
            value={stats.rejected}
            tone={stats.rejected > 0 ? 'var(--warn)' : undefined}
          />
          <Tile
            label="Corrected"
            value={stats.corrected}
            tone={stats.corrected > 0 ? 'var(--bad)' : undefined}
          />
          <Tile
            label="Avg confidence"
            value={`${(stats.avgConfidence * 100).toFixed(0)}%`}
            tone="var(--teal)"
          />
          <Tile label="Throughput / min" value={stats.throughputPerMin.toFixed(2)} tone="var(--teal)" />
          <Tile label="Avg latency" value={`${stats.avgLatencyMs} ms`} tone="var(--violet)" />
        </div>

        {stats.unreviewed > 0 && (
          <div className="panel row" style={{ justifyContent: 'space-between' }}>
            <span>
              <strong style={{ fontSize: 18 }}>{stats.unreviewed.toLocaleString()}</strong>{' '}
              <span className="muted">
                predictions nobody has confirmed — until they are, agreement is measured on the rest
              </span>
            </span>
            <button className="btn primary sm" onClick={() => navigate('/classify')}>
              Review now →
            </button>
          </div>
        )}

        <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)' }}>
          <div className="panel">
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 6 }}>
              <strong>Classifications over time</strong>
              <button className="btn sm" onClick={() => setShowTable((t) => !t)}>
                {showTable ? 'Show chart' : 'Show table'}
              </button>
            </div>

            {showTable ? (
              <table>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Classified</th>
                    <th>Sent to a person</th>
                    <th>Avg latency</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.buckets.map((b) => (
                    <tr key={b.t} style={{ cursor: 'default' }}>
                      <td className="muted">{new Date(b.t).toLocaleString()}</td>
                      <td>{b.predicted}</td>
                      <td>{b.rejected}</td>
                      <td className="muted">{b.avgLatencyMs} ms</td>
                    </tr>
                  ))}
                  {stats.buckets.length === 0 && (
                    <tr>
                      <td colSpan={4} className="muted">
                        No data in this window.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            ) : (
              /*
                Its own series, not the verdict ones renamed. Mapping these
                buckets onto the verdict keys was the first attempt and it drew
                a chart of pork shoulders with GOOD and UNSURE in the legend —
                the wording the whole task separation exists to prevent, arriving
                through a component nobody thought of as carrying wording.
              */
              <StackedColumns
                buckets={stats.buckets}
                series={CLASSIFICATION_SERIES}
                emptyMessage="Nothing classified in this window yet."
                ariaLabel="Classifications over time"
              />
            )}
          </div>

          <div className="panel">
            <strong>What came off the line</strong>
            <p className="muted" style={{ fontSize: 12, margin: '4px 0 14px' }}>
              Counted on the corrected class where a person corrected one.
            </p>
            <ClassBars classes={stats.classes} />
          </div>
        </div>
      </div>
    </section>
  );
}

/** "about 2 days ago" — plain words, because a timestamp alone rarely lands. */
function describeGap(iso: string): string {
  const hours = (Date.now() - new Date(iso).getTime()) / 3_600_000;
  if (hours < 1) return 'less than an hour ago';
  if (hours < 48) return `about ${Math.round(hours)} hours ago`;
  return `about ${Math.round(hours / 24)} days ago`;
}

function Tile({ label, value, tone }: { label: string; value: string | number; tone?: string }) {
  // Setting --tone colours the whole tile — accent rule, tint and number — so a
  // toned tile reads as one thing, not a coloured number on a blue-barred card.
  return (
    <div
      className="stat-tile"
      style={{ flex: '1 1 150px', ...(tone ? { ['--tone']: tone } : {}) } as CSSProperties}
    >
      <div className="stat-label">{label}</div>
      <div className="stat-value">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}
